import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Users, MessageSquare, Send, Loader2, ThumbsUp, Sparkles, Bot, HelpCircle, Lightbulb, MessagesSquare, Megaphone, LifeBuoy, Settings, X, Shield, ShieldOff, Search, Palette, Upload, Paperclip } from 'lucide-react';
import { communitiesApi, postsApi, spacesApi, mediaApi } from '../api';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { Button } from '../components/ui/button';
import CommunityHealthPanel from '../components/aura/CommunityHealthPanel';

const SENTIMENT_BADGES = {
  positive: { color: 'bg-emerald-100 text-emerald-700', key: 'sentiment_positive', threshold: 0.3 },
  neutral: { color: 'bg-slate-100 text-slate-600', key: 'sentiment_neutral', threshold: -0.3 },
  negative: { color: 'bg-rose-100 text-rose-700', key: 'sentiment_negative', threshold: -1.0 },
};

const CLASSIFICATION = {
  question: { icon: HelpCircle, color: 'bg-amber-100 text-amber-700', key: 'post_classification_question' },
  idea: { icon: Lightbulb, color: 'bg-indigo-100 text-indigo-700', key: 'post_classification_idea' },
  discussion: { icon: MessagesSquare, color: 'bg-slate-100 text-slate-700', key: 'post_classification_discussion' },
  help: { icon: LifeBuoy, color: 'bg-rose-100 text-rose-700', key: 'post_classification_help' },
  announcement: { icon: Megaphone, color: 'bg-emerald-100 text-emerald-700', key: 'post_classification_announcement' },
};

function getSentimentBadge(score) {
  if (score === null || score === undefined) return null;
  if (score >= SENTIMENT_BADGES.positive.threshold) return SENTIMENT_BADGES.positive;
  if (score >= SENTIMENT_BADGES.neutral.threshold) return SENTIMENT_BADGES.neutral;
  return SENTIMENT_BADGES.negative;
}

function timeAgo(iso) {
  const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutes < 1) return 'teraz';
  if (minutes < 60) return `${minutes} min temu`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h temu`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d temu`;
  return new Date(iso).toLocaleDateString('pl-PL');
}

function PostCard({ post, currentUserId, onMutate }) {
  const { t } = useTranslation();
  const [comments, setComments] = useState([]);
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [sending, setSending] = useState(false);
  const [likes, setLikes] = useState(post.likes_count || 0);
  const [liked, setLiked] = useState(false);
  const [readStats, setReadStats] = useState(null);
  // Q&A — score, my vote, accepted_answer_id
  const [score, setScore] = useState(post.score || 0);
  const [myVote, setMyVote] = useState(post._my_vote || 0);
  const [acceptedAnswerId, setAcceptedAnswerId] = useState(post.accepted_answer_id || null);
  const isAnnouncement = post.type === 'announcement';
  const isQuestion = post.type === 'question';
  const isOwnPost = post.author_id === currentUserId;
  const cardRef = React.useRef(null);

  // Auto mark-read dla ogłoszeń gdy post wjedzie w viewport (IntersectionObserver).
  // Wykonujemy tylko raz per post per session.
  React.useEffect(() => {
    if (!isAnnouncement || isOwnPost) return;
    if (typeof IntersectionObserver === 'undefined') return;
    const node = cardRef.current;
    if (!node) return;
    const observed = { done: false };
    const obs = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting && !observed.done) {
          observed.done = true;
          postsApi.markRead(post.id).catch(() => {});
          obs.disconnect();
        }
      }
    }, { threshold: 0.5 });
    obs.observe(node);
    return () => obs.disconnect();
  }, [isAnnouncement, isOwnPost, post.id]);

  // Read stats dla autora ogłoszenia
  React.useEffect(() => {
    if (!isAnnouncement || !isOwnPost) return;
    postsApi.readStats(post.id).then(r => setReadStats(r.data)).catch(() => {});
  }, [isAnnouncement, isOwnPost, post.id]);

  const loadComments = async () => {
    try {
      const r = await postsApi.getComments(post.id);
      setComments(r.data || []);
    } catch (e) { console.error(e); }
  };

  const toggleComments = async () => {
    if (!showComments) await loadComments();
    setShowComments(!showComments);
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || sending) return;
    setSending(true);
    try {
      await postsApi.addComment(post.id, newComment.trim());
      setNewComment('');
      await loadComments();
    } catch (err) {
      alert('Komentarz: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSending(false);
    }
  };

  const toggleLike = async () => {
    try {
      const r = await postsApi.toggleReaction(post.id, 'like');
      const newLiked = r.data?.reacted ?? !liked;
      setLiked(newLiked);
      setLikes(prev => prev + (newLiked ? 1 : -1));
    } catch (e) { console.error(e); }
  };

  // Q&A — vote (1, -1, 0). Toggle: ponowne kliknięcie cofa.
  const voteOnPost = async (value) => {
    try {
      const newValue = myVote === value ? 0 : value;
      const r = await postsApi.vote(post.id, newValue);
      setMyVote(r.data?.value ?? newValue);
      setScore(r.data?.score ?? score);
    } catch (e) { console.error(e); }
  };

  const voteOnComment = async (commentId, value) => {
    try {
      const myVote = comments.find(c => c.id === commentId)?._my_vote || 0;
      const newValue = myVote === value ? 0 : value;
      const r = await postsApi.voteComment(post.id, commentId, newValue);
      setComments(prev => prev.map(c => c.id === commentId ? { ...c, _my_vote: r.data?.value ?? newValue, score: r.data?.score ?? c.score ?? 0 } : c));
    } catch (e) { console.error(e); }
  };

  const acceptAnswer = async (commentId) => {
    try {
      const newCommentId = acceptedAnswerId === commentId ? null : commentId;
      const r = await postsApi.acceptAnswer(post.id, newCommentId);
      setAcceptedAnswerId(r.data?.accepted_answer_id || null);
      setComments(prev => prev.map(c => ({ ...c, is_accepted_answer: c.id === r.data?.accepted_answer_id })));
    } catch (e) {
      alert('Tylko autor pytania może oznaczyć odpowiedź');
      console.error(e);
    }
  };

  const sentBadge = getSentimentBadge(post.sentiment_score);
  const cls = CLASSIFICATION[post.ai_classification];
  const ClsIcon = cls?.icon;

  return (
    <article ref={cardRef} className={`bg-white rounded-xl border p-4 ${isAnnouncement ? 'border-emerald-300 bg-emerald-50/30' : 'border-slate-200'}`}>
      {isAnnouncement && (
        <div className="flex items-center justify-between mb-2 -mt-1 -mx-1 px-2 py-1 bg-emerald-100 rounded text-xs text-emerald-800">
          <span className="font-semibold flex items-center gap-1">
            <Megaphone className="w-3.5 h-3.5" /> Ogłoszenie
          </span>
          {readStats && isOwnPost && (
            <span className="text-emerald-700">
              {readStats.reads_count} / {readStats.total_members} przeczytało ({readStats.pct}%)
            </span>
          )}
        </div>
      )}
      <header className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2 text-sm">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white text-xs font-semibold">
            {(post.author_name || '?').charAt(0).toUpperCase()}
          </div>
          <span className="font-medium text-slate-900">{post.author_name || 'Użytkownik'}</span>
          <span className="text-slate-400">•</span>
          <span className="text-slate-500">{timeAgo(post.created_at)}</span>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {isQuestion && (
            <span className="text-[10px] px-2 py-0.5 rounded-full font-medium inline-flex items-center gap-1 bg-amber-100 text-amber-800">
              <HelpCircle className="w-3 h-3" /> Pytanie {acceptedAnswerId ? '✓' : ''}
            </span>
          )}
          {cls && (
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium inline-flex items-center gap-1 ${cls.color}`} title="AI">
              <ClsIcon className="w-3 h-3" /> {t(`community_detail.${cls.key}`)}
            </span>
          )}
          {sentBadge && (
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${sentBadge.color}`} title={`AI: ${post.sentiment_score?.toFixed(2)}`}>
              {t(`community_detail.${sentBadge.key}`)}
            </span>
          )}
        </div>
      </header>

      <p className="text-slate-800 whitespace-pre-wrap">{post.content}</p>

      {post.image_url && (
        <a href={post.image_url} target="_blank" rel="noopener noreferrer" className="block mt-2">
          <img src={post.image_url} alt="" className="max-h-96 rounded-lg border border-slate-200" loading="lazy" />
        </a>
      )}
      {post.video_url && (
        <div className="mt-2">
          {/youtube\.com|youtu\.be|vimeo\.com/.test(post.video_url) ? (
            <iframe
              src={post.video_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
              className="w-full aspect-video rounded-lg border border-slate-200"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title="video"
            />
          ) : (
            <video controls src={post.video_url} className="w-full max-h-96 rounded-lg border border-slate-200" />
          )}
        </div>
      )}

      {post.attachments && post.attachments.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {post.attachments.map((a, i) => (
            <a
              key={i}
              href={a.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded text-xs"
            >
              <Paperclip className="w-3 h-3" /> {a.name || 'plik'}
            </a>
          ))}
        </div>
      )}

      {post.ai_summary && (
        <div className="mt-2 px-3 py-1.5 bg-indigo-50/50 border border-indigo-100 rounded text-xs text-indigo-700 flex items-center gap-1">
          <Sparkles className="w-3 h-3" />
          <span className="font-medium">{t('community_detail.ai_summary')}:</span> {post.ai_summary}
        </div>
      )}

      {post.ai_tags && post.ai_tags.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {post.ai_tags.map((t, i) => (
            <span key={i} className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded">#{t}</span>
          ))}
        </div>
      )}

      <footer className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-100 text-sm text-slate-500">
        {isQuestion ? (
          <div className="flex items-center gap-1 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
            <button onClick={() => voteOnPost(1)} className={`flex items-center justify-center w-6 h-6 rounded-full hover:bg-amber-200 ${myVote === 1 ? 'text-emerald-700' : 'text-slate-500'}`} title="Podbij pytanie">
              ▲
            </button>
            <span className={`font-bold min-w-[20px] text-center ${score > 0 ? 'text-emerald-700' : score < 0 ? 'text-rose-600' : 'text-slate-700'}`}>{score}</span>
            <button onClick={() => voteOnPost(-1)} className={`flex items-center justify-center w-6 h-6 rounded-full hover:bg-amber-200 ${myVote === -1 ? 'text-rose-600' : 'text-slate-500'}`} title="Obniż pytanie">
              ▼
            </button>
          </div>
        ) : (
          <button onClick={toggleLike} className={`flex items-center gap-1 ${liked ? 'text-indigo-600' : 'hover:text-indigo-600'}`}>
            <ThumbsUp className={`w-4 h-4 ${liked ? 'fill-current' : ''}`} /> {likes}
          </button>
        )}
        <button onClick={toggleComments} className="flex items-center gap-1 hover:text-indigo-600">
          <MessageSquare className="w-4 h-4" /> {post.comments_count || comments.length}
          {isQuestion && acceptedAnswerId && <span className="ml-1 text-emerald-600" title="Zaakceptowana odpowiedź">✓</span>}
        </button>
      </footer>

      {showComments && (
        <div className="mt-3 pt-3 border-t border-slate-100 space-y-2">
          {comments.map(c => {
            const accepted = c.is_accepted_answer;
            return (
              <div key={c.id} className={`px-3 py-2 rounded-lg text-sm ${
                accepted ? 'bg-emerald-50 border-2 border-emerald-300' :
                c.is_ai_generated ? 'bg-indigo-50 border border-indigo-200' : 'bg-slate-50'
              }`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-1 text-xs text-slate-500">
                    {c.is_ai_generated ? (
                      <><Bot className="w-3 h-3 text-indigo-500" /> <span className="font-medium text-indigo-700">{t('community_detail.ai_moderator_name')}</span></>
                    ) : (
                      <span className="font-medium">{c.author_name || 'User'}</span>
                    )}
                    <span>•</span>
                    <span>{timeAgo(c.created_at)}</span>
                    {accepted && (
                      <span className="ml-2 text-emerald-700 font-semibold flex items-center gap-1">
                        ✓ Zaakceptowana odpowiedź
                      </span>
                    )}
                  </div>
                  {isQuestion && (
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-0.5 text-xs">
                        <button onClick={() => voteOnComment(c.id, 1)} className={`flex items-center justify-center w-5 h-5 hover:bg-slate-200 rounded ${c._my_vote === 1 ? 'text-emerald-700' : 'text-slate-500'}`}>▲</button>
                        <span className={`font-bold min-w-[16px] text-center ${(c.score || 0) > 0 ? 'text-emerald-700' : (c.score || 0) < 0 ? 'text-rose-600' : ''}`}>{c.score || 0}</span>
                        <button onClick={() => voteOnComment(c.id, -1)} className={`flex items-center justify-center w-5 h-5 hover:bg-slate-200 rounded ${c._my_vote === -1 ? 'text-rose-600' : 'text-slate-500'}`}>▼</button>
                      </div>
                      {isOwnPost && !c.is_ai_generated && (
                        <button
                          onClick={() => acceptAnswer(c.id)}
                          className={`text-xs px-2 py-0.5 rounded ${accepted ? 'bg-emerald-600 text-white' : 'bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-50'}`}
                          title={accepted ? 'Cofnij akceptację' : 'Oznacz jako odpowiedź'}
                        >
                          {accepted ? '✓ Zaakceptowane' : 'Zaakceptuj'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div className="text-slate-800 whitespace-pre-wrap">{c.content}</div>
              </div>
            );
          })}

          <form onSubmit={submitComment} className="flex gap-2 mt-2">
            <input
              value={newComment}
              onChange={e => setNewComment(e.target.value)}
              placeholder={t('community_detail.comment_placeholder')}
              disabled={sending}
              className="flex-1 px-3 py-1.5 border border-slate-200 rounded text-sm"
            />
            <button type="submit" disabled={!newComment.trim() || sending} className="px-3 py-1.5 bg-indigo-600 text-white rounded text-sm hover:bg-indigo-700 disabled:opacity-50">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </form>
        </div>
      )}
    </article>
  );
}

function CommunitySettingsModal({ community, onClose, onSaved }) {
  const [allowStandalone, setAllowStandalone] = React.useState(!!community.allow_standalone_access);
  const [allowMemberPosts, setAllowMemberPosts] = React.useState(community.allow_member_posts !== false);
  const [requireApproval, setRequireApproval] = React.useState(!!community.require_approval);
  // Branding
  const [image, setImage] = React.useState(community.image || '');
  const [coverImage, setCoverImage] = React.useState(community.cover_image || '');
  const [primaryColor, setPrimaryColor] = React.useState(community.custom_branding?.primary_color || '#6366f1');
  const [whiteLabel, setWhiteLabel] = React.useState(!!community.white_label);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = {
        allow_standalone_access: allowStandalone,
        allow_member_posts: allowMemberPosts,
        require_approval: requireApproval,
        image: image.trim() || null,
        cover_image: coverImage.trim() || null,
        custom_branding: { ...(community.custom_branding || {}), primary_color: primaryColor },
        white_label: whiteLabel,
      };
      await communitiesApi.update(community.id, payload);
      onSaved(payload);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl max-w-lg w-full my-8" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Settings className="w-5 h-5 text-slate-500" /> Ustawienia społeczności
          </h2>
          <button type="button" onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-4">

          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900">
            <strong>⚠ Bezpieczeństwo danych firmowych:</strong> Ta sekcja kontroluje czy członkowie
            tej społeczności mogą logować się do platformy poza Twoim CRM. Domyślnie WYŁĄCZONE —
            konta SSO żyją tylko w kontekście CRM, dane firmowe nie wyciekają.
          </div>

          <label className="flex items-start gap-3 p-3 rounded border border-slate-200 hover:bg-slate-50 cursor-pointer">
            <input
              type="checkbox"
              checked={allowStandalone}
              onChange={e => setAllowStandalone(e.target.checked)}
              className="mt-0.5 w-5 h-5"
            />
            <div className="flex-1">
              <div className="font-medium text-slate-900 text-sm flex items-center gap-1">
                {allowStandalone ? <ShieldOff className="w-4 h-4 text-emerald-600" /> : <Shield className="w-4 h-4 text-slate-500" />}
                Zezwól członkom na logowanie się poza CRM
              </div>
              <div className="text-xs text-slate-500 mt-1">
                Gdy WŁĄCZONE: członkowie mogą logować się bezpośrednio (z hasłem) na standalone platformę
                i widzieć tę społeczność niezależnie od Twojego CRM.<br />
                Gdy WYŁĄCZONE (domyślne): konta SSO działają tylko gdy user wchodzi przez Twój CRM —
                najlepsze dla wewnętrznych społeczności firmowych.
              </div>
            </div>
          </label>

          <label className="flex items-start gap-3 p-3 rounded border border-slate-200 hover:bg-slate-50 cursor-pointer">
            <input
              type="checkbox"
              checked={allowMemberPosts}
              onChange={e => setAllowMemberPosts(e.target.checked)}
              className="mt-0.5 w-5 h-5"
            />
            <div className="flex-1">
              <div className="font-medium text-slate-900 text-sm">Członkowie mogą tworzyć posty</div>
              <div className="text-xs text-slate-500 mt-1">
                Gdy wyłączone — tylko owner/admin/moderator publikuje.
              </div>
            </div>
          </label>

          <label className="flex items-start gap-3 p-3 rounded border border-slate-200 hover:bg-slate-50 cursor-pointer">
            <input
              type="checkbox"
              checked={requireApproval}
              onChange={e => setRequireApproval(e.target.checked)}
              className="mt-0.5 w-5 h-5"
            />
            <div className="flex-1">
              <div className="font-medium text-slate-900 text-sm">Wymagaj zatwierdzenia nowych członków</div>
              <div className="text-xs text-slate-500 mt-1">
                Gdy włączone — moderator musi zaakceptować nowych członków przed dostępem.
              </div>
            </div>
          </label>

          <div className="pt-2 border-t border-slate-200">
            <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-1.5">
              <Palette className="w-4 h-4 text-slate-500" /> Wygląd i branding
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Logo społeczności (URL)</label>
                <div className="flex items-center gap-2">
                  <input
                    value={image}
                    onChange={e => setImage(e.target.value)}
                    placeholder="https://..."
                    className="flex-1 px-3 py-1.5 border border-slate-200 rounded text-sm"
                  />
                  {image && <img src={image} alt="" className="w-9 h-9 rounded object-cover border border-slate-200" />}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Baner / cover (URL)</label>
                <input
                  value={coverImage}
                  onChange={e => setCoverImage(e.target.value)}
                  placeholder="https://..."
                  className="w-full px-3 py-1.5 border border-slate-200 rounded text-sm"
                />
                {coverImage && <img src={coverImage} alt="" className="mt-2 w-full h-24 object-cover rounded border border-slate-200" />}
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Kolor wiodący</label>
                <div className="flex items-center gap-2 flex-wrap">
                  <input
                    type="color"
                    value={primaryColor}
                    onChange={e => setPrimaryColor(e.target.value)}
                    className="w-12 h-9 border border-slate-300 rounded cursor-pointer"
                  />
                  <input
                    value={primaryColor}
                    onChange={e => setPrimaryColor(e.target.value)}
                    className="w-28 px-2 py-1.5 border border-slate-200 rounded text-sm font-mono"
                  />
                  <span className="text-xs text-slate-400">akcent w nagłówku społeczności</span>
                </div>
              </div>
              <label className="flex items-start gap-2 text-sm text-slate-700 pt-1">
                <input
                  type="checkbox"
                  checked={whiteLabel}
                  onChange={e => setWhiteLabel(e.target.checked)}
                  className="mt-0.5 w-4 h-4"
                />
                <span>
                  White-label — ukryj „Powered by Aurabase"
                  <span className="block text-xs text-slate-400">Wymaga planu Enterprise właściciela społeczności.</span>
                </span>
              </label>
            </div>
          </div>

          {error && <p className="text-sm text-rose-600">{error}</p>}
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-slate-200">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded">Anuluj</button>
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-1 px-4 py-2 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Zapisz
          </button>
        </div>
      </form>
    </div>
  );
}


function NewSpaceModal({ onClose, onCreate }) {
  const [name, setName] = React.useState('');
  const [icon, setIcon] = React.useState('💬');
  const [description, setDescription] = React.useState('');
  const [color, setColor] = React.useState('#6366f1');
  const [saving, setSaving] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || saving) return;
    setSaving(true);
    try {
      await onCreate({ name: name.trim(), icon: icon || undefined, description: description.trim() || undefined, color });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl max-w-md w-full my-8" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold">Nowy kanał</h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600">×</button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">Nazwa *</label>
            <input value={name} onChange={e => setName(e.target.value)} required maxLength={50}
                   placeholder="np. Ogłoszenia, Pytania, Off-topic"
                   className="w-full px-3 py-2 border border-slate-300 rounded text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-sm font-medium mb-1">Ikona (emoji)</label>
              <input value={icon} onChange={e => setIcon(e.target.value)} maxLength={2}
                     className="w-full px-3 py-2 border border-slate-300 rounded text-sm text-center" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Kolor</label>
              <input type="color" value={color} onChange={e => setColor(e.target.value)}
                     className="w-full h-9 border border-slate-300 rounded" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Opis (opcjonalnie)</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} maxLength={500} rows={2}
                      className="w-full px-3 py-2 border border-slate-300 rounded text-sm resize-none" />
          </div>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-slate-200">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded">Anuluj</button>
          <button type="submit" disabled={!name.trim() || saving}
                  className="px-4 py-2 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50">
            {saving ? '...' : 'Utwórz kanał'}
          </button>
        </div>
      </form>
    </div>
  );
}

const MEMBER_ROLE_BADGE = {
  owner: { label: 'Owner', cls: 'bg-emerald-100 text-emerald-700' },
  admin: { label: 'Admin', cls: 'bg-indigo-100 text-indigo-700' },
  moderator: { label: 'Moderator', cls: 'bg-amber-100 text-amber-700' },
  member: { label: 'Członek', cls: 'bg-slate-100 text-slate-600' },
};

function ProfileTags({ label, items }) {
  return (
    <div className="flex items-start gap-2 text-xs">
      <span className="text-slate-400 shrink-0 w-24">{label}:</span>
      <div className="flex flex-wrap gap-1">
        {items.map((it, i) => (
          <span key={i} className="px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded">{it}</span>
        ))}
      </div>
    </div>
  );
}

function MembersDirectoryModal({ communityId, currentUserId, onClose }) {
  const navigate = useNavigate();
  const [members, setMembers] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState('');
  const [role, setRole] = React.useState('');
  const [sort, setSort] = React.useState('name');
  const [expandedId, setExpandedId] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await communitiesApi.getMembers(communityId, {
          search: search.trim() || undefined,
          role: role || undefined,
          sort,
          limit: 200,
        });
        if (!cancelled) {
          setMembers(r.data?.items || []);
          setTotal(r.data?.total || 0);
        }
      } catch (e) {
        if (!cancelled) { setMembers([]); setTotal(0); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, search ? 300 : 0);  // debounce tylko przy wyszukiwaniu
    return () => { cancelled = true; clearTimeout(timer); };
  }, [communityId, search, role, sort]);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full my-8" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Users className="w-5 h-5 text-slate-500" /> Członkowie społeczności
            <span className="text-sm font-normal text-slate-400">({total})</span>
          </h2>
          <button type="button" onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 border-b border-slate-200 flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Szukaj po nazwie lub emailu..."
              className="w-full pl-8 pr-3 py-1.5 border border-slate-200 rounded text-sm"
            />
          </div>
          <select value={role} onChange={e => setRole(e.target.value)}
                  className="px-2 py-1.5 border border-slate-200 rounded text-sm bg-white">
            <option value="">Wszystkie role</option>
            <option value="owner">Owner</option>
            <option value="admin">Admin</option>
            <option value="moderator">Moderator</option>
            <option value="member">Członek</option>
          </select>
          <select value={sort} onChange={e => setSort(e.target.value)}
                  className="px-2 py-1.5 border border-slate-200 rounded text-sm bg-white">
            <option value="name">Nazwa A-Z</option>
            <option value="joined">Najnowsi</option>
            <option value="aura">Aura Score</option>
            <option value="posts">Aktywność (posty)</option>
          </select>
        </div>

        <div className="p-2 max-h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
            </div>
          ) : members.length === 0 ? (
            <p className="text-center py-12 text-sm text-slate-500">Brak członków spełniających kryteria.</p>
          ) : (
            members.map(m => {
              const u = m.user || {};
              const badge = MEMBER_ROLE_BADGE[m.role] || MEMBER_ROLE_BADGE.member;
              const expanded = expandedId === m.id;
              const isMe = u.id === currentUserId;
              return (
                <div key={m.id} className="rounded-lg hover:bg-slate-50">
                  <button
                    type="button"
                    onClick={() => setExpandedId(expanded ? null : m.id)}
                    className="w-full flex items-center gap-3 p-2.5 text-left"
                  >
                    {u.avatar ? (
                      <img src={u.avatar} alt="" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white text-sm font-semibold">
                        {(u.name || '?').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900 truncate">{u.name || 'Użytkownik'}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${badge.cls}`}>{badge.label}</span>
                      </div>
                      {u.bio && <p className="text-xs text-slate-500 truncate">{u.bio}</p>}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate-500 shrink-0">
                      <span title="Aura Score" className="flex items-center gap-1">
                        <Sparkles className="w-3.5 h-3.5 text-indigo-400" /> {u.aura_score ?? 50}
                      </span>
                      <span title="Posty w tej społeczności" className="flex items-center gap-1">
                        <MessageSquare className="w-3.5 h-3.5" /> {m.posts_count || 0}
                      </span>
                    </div>
                  </button>

                  {expanded && (
                    <div className="pl-14 pr-2.5 pb-3 space-y-2">
                      {u.skills?.length > 0 && <ProfileTags label="Umiejętności" items={u.skills} />}
                      {u.interests?.length > 0 && <ProfileTags label="Zainteresowania" items={u.interests} />}
                      {u.goals?.length > 0 && <ProfileTags label="Cele" items={u.goals} />}
                      {u.looking_for?.length > 0 && <ProfileTags label="Szuka" items={u.looking_for} />}
                      {!u.skills?.length && !u.interests?.length && !u.goals?.length && !u.looking_for?.length && (
                        <p className="text-xs text-slate-400">Brak danych profilu.</p>
                      )}
                      <div className="flex items-center gap-3 pt-1 text-xs">
                        <span className="text-slate-400">
                          Dołączył: {m.joined_at ? new Date(m.joined_at).toLocaleDateString('pl-PL') : '—'}
                        </span>
                        {!isMe && u.id && (
                          <button
                            type="button"
                            onClick={() => navigate(`/dashboard/messages?with=${u.id}`)}
                            className="ml-auto flex items-center gap-1 px-2.5 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                          >
                            <Send className="w-3.5 h-3.5" /> Wyślij wiadomość
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

export default function CommunityDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  // ?filter=question + ?sort=top → Q&A view (Sorto-CRM Społeczność tab "Q&A")
  const filterType = searchParams.get('filter') === 'question' ? 'question' : null;
  const sortOrder = searchParams.get('sort') === 'top' ? 'top' : 'recent';
  const { user } = useAuth();
  const [community, setCommunity] = useState(null);
  const [posts, setPosts] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [activeSpaceId, setActiveSpaceId] = useState('');   // '' = Wszystko, '__root__' = bez space, <id> = konkretny space
  const [showNewSpace, setShowNewSpace] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [myRole, setMyRole] = useState(null);  // { role, is_admin, is_member }
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [showMedia, setShowMedia] = useState(false);
  const [uploadingKind, setUploadingKind] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [composerSpaceId, setComposerSpaceId] = useState(''); // gdzie publikować
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [cRes, pRes, sRes, rRes] = await Promise.all([
        communitiesApi.get(id),
        postsApi.list(id, 0, 30, activeSpaceId || null, filterType, sortOrder),
        spacesApi.list(id).catch(() => ({ data: { items: [] } })),
        communitiesApi.myRole(id).catch(() => ({ data: { is_admin: false } })),
      ]);
      setCommunity(cRes.data);
      setPosts(pRes.data || []);
      setSpaces(sRes.data?.items || []);
      setMyRole(rRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id, activeSpaceId, filterType, sortOrder]);

  const handleCreateSpace = async (data) => {
    try {
      await spacesApi.create(id, data);
      const sRes = await spacesApi.list(id);
      setSpaces(sRes.data?.items || []);
      setShowNewSpace(false);
    } catch (err) {
      alert('Space: ' + (err?.response?.data?.detail || err.message));
    }
  };

  const handleDeleteSpace = async (spaceId) => {
    if (!confirm('Usunąć kanał? Posty wrócą do "Wszystko".')) return;
    try {
      await spacesApi.remove(spaceId);
      if (activeSpaceId === spaceId) setActiveSpaceId('');
      const sRes = await spacesApi.list(id);
      setSpaces(sRes.data?.items || []);
    } catch (err) {
      alert('Space delete: ' + (err?.response?.data?.detail || err.message));
    }
  };

  const handleMediaUpload = async (e, kind) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setUploadingKind(kind);
    try {
      for (const file of files) {
        const d = (await mediaApi.upload(file)).data || {};
        if (kind === 'image') setImageUrl(d.url || '');
        else if (kind === 'video') setVideoUrl(d.url || '');
        else setAttachments(prev => [...prev, { url: d.url, name: d.name, size: d.size }]);
      }
    } catch (err) {
      alert('Upload nie powiódł się: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setUploadingKind(null);
    }
  };

  const submitPost = async (e) => {
    e.preventDefault();
    if (!content.trim() || sending) return;
    setSending(true);
    try {
      await postsApi.create({
        community_id: id,
        content: content.trim(),
        type: 'discussion',
        image_url: imageUrl.trim() || undefined,
        video_url: videoUrl.trim() || undefined,
        attachments: attachments.length ? attachments : undefined,
        space_id: composerSpaceId || (activeSpaceId && activeSpaceId !== '__root__' ? activeSpaceId : undefined),
      });
      setContent(''); setImageUrl(''); setVideoUrl(''); setAttachments([]); setShowMedia(false);
      await load();
    } catch (err) {
      alert('Nie udało się opublikować: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Link to="/dashboard/communities" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 mb-4">
        <ArrowLeft className="w-4 h-4" /> {t('community_detail.back_to_communities')}
      </Link>

      {community && (
        <div
          className="bg-white rounded-xl border border-slate-200 p-6 mb-6"
          style={community.custom_branding?.primary_color ? { borderTopWidth: 3, borderTopColor: community.custom_branding.primary_color } : undefined}
        >
          {community.cover_image && (
            <img
              src={community.cover_image}
              alt=""
              className="-mt-6 -mx-6 mb-4 w-[calc(100%+3rem)] h-32 object-cover rounded-t-xl"
            />
          )}
          <div className="flex items-start gap-4">
            {community.image && (
              <img src={community.image} alt="" className="w-20 h-20 rounded-lg object-cover" />
            )}
            <div className="flex-1">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">{community.name}</h1>
                  <p className="text-slate-600 mt-1">{community.description}</p>
                </div>
                {myRole?.is_admin && (
                  <button
                    onClick={() => setShowSettings(true)}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm border border-slate-300 rounded hover:bg-slate-50 text-slate-700"
                    title="Ustawienia społeczności (tylko owner/admin/moderator)"
                  >
                    <Settings className="w-4 h-4" /> Ustawienia
                  </button>
                )}
              </div>
              <div className="flex gap-4 mt-3 text-sm text-slate-500 flex-wrap">
                <button
                  onClick={() => setShowMembers(true)}
                  className="flex items-center gap-1 hover:text-indigo-600 transition-colors"
                  title="Zobacz katalog członków"
                >
                  <Users className="w-4 h-4" /> {t('community_detail.members_count', { count: community.members_count || 0 })}
                </button>
                <span className="flex items-center gap-1"><MessageSquare className="w-4 h-4" /> {t('community_detail.posts_count', { count: community.posts_count || 0 })}</span>
                {community.allow_standalone_access ? (
                  <span className="flex items-center gap-1 text-emerald-600" title="Członkowie mogą logować się do społeczności standalone">
                    <ShieldOff className="w-4 h-4" /> Standalone access: ON
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-slate-500" title="Konta SSO działają tylko przez CRM">
                    <Shield className="w-4 h-4" /> SSO-only
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showSettings && community && myRole?.is_admin && (
        <CommunitySettingsModal
          community={community}
          onClose={() => setShowSettings(false)}
          onSaved={(updated) => {
            setCommunity({ ...community, ...updated });
            setShowSettings(false);
          }}
        />
      )}

      {showMembers && community && (
        <MembersDirectoryModal
          communityId={id}
          currentUserId={user?.id}
          onClose={() => setShowMembers(false)}
        />
      )}

      {/* Tabs: Spaces (kanały tematyczne) */}
      <div className="bg-white rounded-xl border border-slate-200 p-3 mb-4 flex items-center gap-2 flex-wrap">
        <button
          onClick={() => setActiveSpaceId('')}
          className={`px-3 py-1.5 text-sm rounded-lg ${activeSpaceId === '' ? 'bg-indigo-100 text-indigo-700 font-semibold' : 'text-slate-600 hover:bg-slate-50'}`}
        >
          Wszystko
        </button>
        {spaces.map(s => (
          <button
            key={s.id}
            onClick={() => setActiveSpaceId(s.id)}
            className={`px-3 py-1.5 text-sm rounded-lg inline-flex items-center gap-1 ${activeSpaceId === s.id ? 'bg-indigo-100 text-indigo-700 font-semibold' : 'text-slate-600 hover:bg-slate-50'}`}
            style={s.color ? { borderLeft: `3px solid ${s.color}` } : undefined}
          >
            {s.icon && <span>{s.icon}</span>}
            {s.name}
            <span className="text-[10px] text-slate-400 ml-0.5">{s.posts_count || 0}</span>
          </button>
        ))}
        <button
          onClick={() => setShowNewSpace(true)}
          className="ml-auto px-2 py-1 text-xs text-indigo-600 hover:text-indigo-700 border border-dashed border-indigo-300 rounded inline-flex items-center gap-1"
          title="Tylko owner/admin może dodać kanał"
        >
          + Nowy kanał
        </button>
      </div>

      {showNewSpace && (
        <NewSpaceModal
          onClose={() => setShowNewSpace(false)}
          onCreate={handleCreateSpace}
        />
      )}

      <form onSubmit={submitPost} className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
        <textarea
          value={content}
          onChange={e => setContent(e.target.value)}
          placeholder={t('community_detail.compose_placeholder')}
          rows={3}
          disabled={sending}
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        {showMedia && (
          <div className="mt-2 space-y-2">
            <div className="flex items-center gap-2">
              <label className={`shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 text-xs border border-slate-300 rounded cursor-pointer hover:bg-slate-50 ${uploadingKind ? 'opacity-50 pointer-events-none' : ''}`}>
                <Upload className="w-3.5 h-3.5" />
                {uploadingKind === 'image' ? 'Wgrywam...' : 'Wgraj obraz'}
                <input type="file" accept="image/*" className="hidden" onChange={e => handleMediaUpload(e, 'image')} />
              </label>
              <input
                value={imageUrl}
                onChange={e => setImageUrl(e.target.value)}
                placeholder="lub wklej URL obrazu"
                className="flex-1 px-3 py-1.5 border border-slate-200 rounded text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className={`shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 text-xs border border-slate-300 rounded cursor-pointer hover:bg-slate-50 ${uploadingKind ? 'opacity-50 pointer-events-none' : ''}`}>
                <Upload className="w-3.5 h-3.5" />
                {uploadingKind === 'video' ? 'Wgrywam...' : 'Wgraj video'}
                <input type="file" accept="video/*" className="hidden" onChange={e => handleMediaUpload(e, 'video')} />
              </label>
              <input
                value={videoUrl}
                onChange={e => setVideoUrl(e.target.value)}
                placeholder="lub URL / embed YouTube/Vimeo"
                className="flex-1 px-3 py-1.5 border border-slate-200 rounded text-sm"
              />
            </div>
            <div>
              <label className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs border border-slate-300 rounded cursor-pointer hover:bg-slate-50 ${uploadingKind ? 'opacity-50 pointer-events-none' : ''}`}>
                <Paperclip className="w-3.5 h-3.5" />
                {uploadingKind === 'file' ? 'Wgrywam...' : 'Załącz plik'}
                <input type="file" multiple className="hidden" onChange={e => handleMediaUpload(e, 'file')} />
              </label>
              {attachments.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {attachments.map((a, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">
                      <Paperclip className="w-3 h-3" /> {a.name}
                      <button
                        type="button"
                        onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))}
                        className="text-slate-400 hover:text-rose-600 ml-0.5"
                      >×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            {imageUrl && <img src={imageUrl} alt="preview" className="max-h-40 rounded border border-slate-200" />}
          </div>
        )}
        <div className="flex items-center justify-between mt-3 gap-2 flex-wrap">
          <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
            <Sparkles className="w-3 h-3" />
            {t('community_detail.compose_hint')}
            <button type="button" onClick={() => setShowMedia(!showMedia)} className="text-indigo-600 hover:text-indigo-700 underline">
              {showMedia ? 'Ukryj media' : '+ Dodaj obraz/video'}
            </button>
          </div>
          <div className="flex items-center gap-2">
            {spaces.length > 0 && (
              <select
                value={composerSpaceId}
                onChange={e => setComposerSpaceId(e.target.value)}
                className="text-xs px-2 py-1 border border-slate-200 rounded bg-white"
                title="Kanał (space) gdzie opublikować"
              >
                <option value="">— Wszystko (root) —</option>
                {spaces.map(s => (
                  <option key={s.id} value={s.id}>{s.icon ? s.icon + ' ' : ''}{s.name}</option>
                ))}
              </select>
            )}
            <Button type="submit" disabled={!content.trim() || sending} className="bg-indigo-600 hover:bg-indigo-700 text-white">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              <span className="ml-2">{t('community_detail.publish')}</span>
            </Button>
          </div>
        </div>
      </form>

      {posts.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>{t('community_detail.empty_posts')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map(post => <PostCard key={post.id} post={post} currentUserId={user?.id} />)}
        </div>
      )}

      {/* Panel zdrowia społeczności — backend zwraca 403 dla nie-adminów, panel cichno znika */}
      <CommunityHealthPanel communityId={id} />

      {!community?.white_label && (
        <p className="text-center text-xs text-slate-400 mt-8">Powered by Aurabase</p>
      )}
    </DashboardLayout>
  );
}
